import { Link } from "react-router-dom";
import { ArrowRight, Layers, AlertTriangle, Sparkles, Circle, Smartphone, BarChart3, Users, Bell, Check } from "lucide-react";

const STAGE_PREVIEW = [
  { name: "Idea", count: 3, color: "#66666E" },
  { name: "Script", count: 3, color: "#4182D9" },
  { name: "Voiceover", count: 1, color: "#B565D9" },
  { name: "Editing", count: 2, color: "#D9683D" },
  { name: "Thumbnail", count: 1, color: "#3DB9D9" },
  { name: "Scheduled", count: 1, color: "#D9A441" },
  { name: "Published", count: 1, color: "#2E8B57" },
];

const PLANS = [
  { name: "Free", tagline: "", price: "$0", period: "", features: ["1 channel", "Full pipeline board", "AI script drafts (5/mo)"], highlight: false },
  { name: "Solo", tagline: "for one creator", price: "$12", period: "/mo", features: ["Unlimited channels", "Cross-channel dashboard", "Unlimited AI drafts", "Performance tracking"], highlight: true },
  { name: "Studio", tagline: "for teams", price: "$39", period: "/mo", features: ["Everything in Solo", "Editors & VA seats", "Roles & permissions", "Priority support"], highlight: false },
];

const HOW = [
  { icon: Layers, title: "Track every video's stage across channels", body: "One board for NightVault, KarmaVault and every channel you run." },
  { icon: AlertTriangle, title: "Get alerted the moment one stalls", body: "A red flag on anything stuck in the same stage more than 3 days." },
  { icon: Sparkles, title: "Draft your next script with AI in one click", body: "Turn a title + niche into a first-draft narration script instantly." },
];

const ROADMAP = [
  { icon: Smartphone, title: "Mobile app (iOS & Android)", body: "Manage your pipeline on the go, wherever inspiration strikes." },
  { icon: BarChart3, title: "Direct YouTube Analytics sync", body: "Auto-pull views and subscribers instead of logging them by hand." },
  { icon: Users, title: "Team collaboration for editors & VAs", body: "Invite your editors and assistants with role-based access." },
  { icon: Bell, title: "Alerts on Discord or email when a video stalls", body: "Get pinged the moment a card sits too long in one stage." },
];

export default function Landing() {
  const maxCount = Math.max(...STAGE_PREVIEW.map((s) => s.count));

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "radial-gradient(1200px 600px at 50% -5%, #16130B 0%, #0C0B0C 42%, #0A0A0B 100%)" }}>
      {/* Ambient amber glow behind hero */}
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-[120px] -translate-x-1/2 w-[820px] h-[520px] rounded-full z-0"
        style={{ background: "radial-gradient(closest-side, rgba(217,164,65,0.16), rgba(217,164,65,0.05) 45%, transparent 72%)", filter: "blur(20px)" }} />

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between max-w-6xl mx-auto px-5 sm:px-8 py-5">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-display font-extrabold">C</div>
          <span className="font-extrabold text-lg tracking-tight no-underline" style={{ fontFamily: "'Manrope', sans-serif" }}>Channel<span className="text-primary">HQ</span></span>
        </div>
        <Link to="/login" data-testid="nav-demo-link"
          className="flex items-center gap-2 text-sm font-medium border border-border rounded-md px-4 py-2 hover:bg-secondary transition-colors duration-200">
          Live Demo <ArrowRight size={15} />
        </Link>
      </nav>

      {/* Hero */}
      <header className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-14 text-center">
        <p className="font-mono text-xs text-primary tracking-[0.28em] uppercase mb-6 fade-up">Multi-channel command center</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl mx-auto fade-up">
          Your Best Channel Is Dying in <span className="text-primary">Silence.</span>
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mt-6 leading-relaxed fade-up">
          ChannelHQ is the command center that tracks every video across your whole channel portfolio — and tells you which one needs you today.
        </p>
        <div className="flex items-center justify-center mt-9 fade-up">
          <Link to="/login" data-testid="explore-demo-button"
            className="flex items-center gap-2 bg-primary text-primary-foreground font-bold rounded-md px-6 py-3.5 text-base hover:bg-[#C29035] transition-colors duration-200">
            Explore the Live Demo <ArrowRight size={18} />
          </Link>
        </div>
      </header>

      {/* Dashboard preview */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <div className="bg-[#121214] border border-primary/20 rounded-xl p-4 sm:p-6 ring-1 ring-primary/5"
          style={{ boxShadow: "0 40px 120px -30px rgba(0,0,0,0.8), 0 0 60px -20px rgba(217,164,65,0.25)" }}>
          <div className="flex items-center gap-2 mb-5 px-1">
            <span className="h-2.5 w-2.5 rounded-full bg-destructive/60" />
            <span className="h-2.5 w-2.5 rounded-full bg-primary/60" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#2E8B57]/60" />
            <span className="font-mono text-[11px] text-muted-foreground ml-3">channelhq / command-center</span>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <PreviewStat label="Active channels" value="2" sub="3 total" />
            <PreviewStat label="In pipeline" value="12" sub="all stages" />
            <PreviewStat label="Due this week" value="8" sub="deadlines" />
            <PreviewStat label="Stalled" value="5" sub=">3 days" danger />
          </div>

          {/* Pipeline bars */}
          <div className="bg-[#0A0A0B] border border-border rounded-lg p-5">
            <p className="font-display font-bold tracking-tight mb-4">Pipeline by stage</p>
            <div className="space-y-2.5">
              {STAGE_PREVIEW.map((s) => (
                <div key={s.name} className="flex items-center gap-3">
                  <span className="w-20 sm:w-24 shrink-0 text-xs sm:text-sm text-muted-foreground">{s.name}</span>
                  <div className="flex-1 h-6 bg-[#121214] rounded-md overflow-hidden border border-border">
                    <div className="h-full rounded-md flex items-center justify-end pr-2 cursor-default transition-[filter,box-shadow] duration-200 hover:brightness-125"
                      style={{ width: `${(s.count / maxCount) * 100}%`, background: s.color, minWidth: "26px" }}>
                      <span className="font-mono text-[11px] font-bold text-black/80">{s.count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid md:grid-cols-3 gap-4">
          {HOW.map((h, i) => (
            <div key={i} className="bg-[#121214] border border-border rounded-lg p-6 hover:border-primary/40 transition-colors duration-200">
              <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center mb-4">
                <h.icon size={20} className="text-primary" />
              </div>
              <h3 className="font-display font-bold text-lg tracking-tight leading-snug mb-2">{h.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{h.body}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-14">
          <Link to="/login" data-testid="footer-demo-button"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold rounded-md px-6 py-3.5 hover:bg-[#C29035] transition-colors duration-200">
            Explore the Live Demo <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-24" data-testid="pricing-section">
        <div className="text-center mb-10">
          <p className="font-mono text-xs text-primary tracking-[0.28em] uppercase mb-3">Pricing</p>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Simple plans for every creator</h2>
          <p className="text-sm text-muted-foreground mt-3">Start free. Upgrade when your portfolio grows.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4 items-start">
          {PLANS.map((p, i) => (
            <div key={i} data-testid={`plan-card-${p.name.toLowerCase()}`}
              className={`rounded-lg p-6 transition-colors duration-200 ${p.highlight ? "bg-[#1A1712] border border-primary/50" : "bg-[#121214] border border-border hover:border-primary/30"}`}>
              {p.highlight && <span className="inline-block font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded bg-primary text-primary-foreground font-bold mb-4">Most popular</span>}
              <h3 className="font-display font-extrabold text-xl tracking-tight">{p.name}</h3>
              {p.tagline && <p className="text-sm text-muted-foreground mb-4">{p.tagline}</p>}
              <div className="flex items-end gap-1 mb-6 mt-4">
                <span className="font-mono text-4xl font-bold tracking-tight">{p.price}</span>
                <span className="font-mono text-sm text-muted-foreground mb-1">{p.period}</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm">
                    <Check size={16} className="text-primary shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/login" data-testid={`plan-cta-${p.name.toLowerCase()}`}
                className={`block text-center font-bold rounded-md px-4 py-2.5 text-sm transition-colors duration-200 ${p.highlight ? "bg-primary text-primary-foreground hover:bg-[#C29035]" : "border border-border text-foreground hover:bg-secondary"}`}>
                {p.name === "Free" ? "Get started free" : "Get started"}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Roadmap */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-24" data-testid="roadmap-section">
        <div className="text-center mb-10">
          <p className="font-mono text-xs text-primary tracking-[0.28em] uppercase mb-3">Roadmap</p>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">What's next</h2>
          <p className="text-sm text-muted-foreground mt-3">Upcoming features on the way — not available yet.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ROADMAP.map((r, i) => (
            <div key={i} data-testid={`roadmap-card-${i}`}
              className="bg-[#121214] border border-border rounded-lg p-6 hover:border-primary/40 transition-colors duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center">
                  <r.icon size={20} className="text-muted-foreground" />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded bg-primary/15 text-primary border border-primary/30">
                  Coming soon
                </span>
              </div>
              <h3 className="font-display font-bold text-base tracking-tight leading-snug mb-2">{r.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 border-t border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs text-muted-foreground" data-testid="footer-copyright">© 2026 ChannelHQ — built for solo creators.</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-200">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="text-xs text-muted-foreground hover:text-primary transition-colors duration-200">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PreviewStat({ label, value, sub, danger }) {
  return (
    <div className={`bg-[#0A0A0B] border rounded-lg p-4 cursor-default transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 ${danger ? "border-destructive/40 hover:border-destructive/70 hover:shadow-[0_14px_34px_-12px_rgba(217,61,61,0.4)]" : "border-border hover:border-primary/50 hover:shadow-[0_14px_34px_-12px_rgba(217,164,65,0.4)]"}`}>
      {danger ? <AlertTriangle size={16} className="text-destructive" /> : <Circle size={10} className="text-primary" fill="currentColor" />}
      <p className="font-mono text-3xl font-bold mt-3 tracking-tight">{value}</p>
      <p className="text-xs font-medium mt-1">{label}</p>
      <p className="text-[11px] text-muted-foreground">{sub}</p>
    </div>
  );
}
